import { useCallback, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchReviewsSummary } from "./api";
import { ReviewsBadge } from "./ReviewsBadge";
import type { ReviewsProps } from "./types";

const DEFAULT_API_URL = "https://api.baluu.io";

export function Reviews({
  businessSlug,
  apiUrl = DEFAULT_API_URL,
  listingId,
  listingType = "course",
  scope = "business",
  theme: _theme = "light",
}: ReviewsProps) {
  const dialogRef = useRef<HTMLElement | null>(null);

  // Fetch summary data
  const { data: summary, isLoading } = useQuery({
    queryKey: ["reviews-summary", businessSlug, scope, listingId, listingType],
    queryFn: () => fetchReviewsSummary({ apiUrl, businessSlug, scope, listingId, listingType }),
    enabled: !!businessSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleCloseDialog = useCallback(() => {
    // Remove the dialog overlay from the DOM
    if (dialogRef.current && dialogRef.current.parentNode) {
      dialogRef.current.parentNode.removeChild(dialogRef.current);
      dialogRef.current = null;
    }
  }, []);

  const handleOpenDialog = useCallback(() => {
    // Create the reviews dialog web component
    const dialog = document.createElement("baluu-reviews-dialog");

    // Set attributes
    dialog.setAttribute("business-slug", businessSlug);
    if (apiUrl) dialog.setAttribute("api-url", apiUrl);
    if (scope) dialog.setAttribute("scope", scope);
    if (listingId) dialog.setAttribute("listing-id", listingId);
    if (listingType) dialog.setAttribute("listing-type", listingType);

    // Store reference
    dialogRef.current = dialog;

    // Append to body (outside any Shadow DOM)
    document.body.appendChild(dialog);

    // Listen for close event from the dialog
    const handleDialogClose = () => {
      handleCloseDialog();
    };

    document.addEventListener("baluu:reviews-close", handleDialogClose, { once: true });
  }, [businessSlug, apiUrl, scope, listingId, listingType, handleCloseDialog]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dialogRef.current && dialogRef.current.parentNode) {
        dialogRef.current.parentNode.removeChild(dialogRef.current);
      }
    };
  }, []);

  if (!businessSlug) {
    return null;
  }

  const defaultSummary = {
    averageRating: 0,
    reviewCount: 0,
    scope: scope,
    listingTitle: null,
  };

  return (
    <div className="baluu-reviews inline-block">
      <ReviewsBadge
        summary={summary || defaultSummary}
        onClick={handleOpenDialog}
        isLoading={isLoading}
      />
    </div>
  );
}
